import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RfidTagIcon } from '../../../assets/icons/rfid-icon.svg';
import { ReactComponent as StationIcon } from '../../../assets/icons/station-icon-view.svg';
import { ReactComponent as UserIcon } from '../../../assets/icons/user-icon.svg';
import {
  CardPool,
  CardPoolKind,
  CustomerUser,
  EmspStation,
  RfidToken,
} from '../../../interface/rfid-management.interface';
import { useAppDispatch } from '../../../redux/hooks';
import { updateTokenApi } from '../../../redux/slices/RfidTokenSlice';
import MyButton from '../../../shared-components/button/Button';
import MultiSelectDropdown, {
  Option,
} from '../../../shared-components/multi-select-field/MultiSelectField';
import SelectField from '../../../shared-components/select-field/SelectField';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import rfidApi from '../../../utils/api/rfid';

interface TokenDetailsModalProperties {
  token: RfidToken;
  tokenId?: string;
  onClose: () => void;
  onDeactivate?: (token: RfidToken) => void;
  onRecharge?: (group: CardPool) => void;
  onUpdate?: (updatedToken: RfidToken) => void;
  isEditing?: boolean;
  onEditModeChange?: (editing: boolean) => void;
}

/**
 * Safely extracts numeric balance in kWh from decimal object, number, or string
 * @param {any} balanceKwh - Raw energy balance value
 * @returns {number} Formatted numeric balance in kWh
 */
const getNumericBalance = (balanceKwh: any): number => {
  if (typeof balanceKwh === 'object' && balanceKwh !== null && '$numberDecimal' in balanceKwh) {
    return Number.parseFloat(balanceKwh.$numberDecimal) || 0;
  }
  if (typeof balanceKwh === 'number') {
    return balanceKwh;
  }
  if (typeof balanceKwh === 'string') {
    return Number.parseFloat(balanceKwh) || 0;
  }
  return 0;
};

/**
 * Resolves the full tenant network station ID (e.g. IN:ENR:1bfd0119-...)
 * @param {EmspStation} station - Station object
 * @returns {string} Fully qualified station ID
 */
const getFullStationId = (station: EmspStation): string => {
  const baseId = station.stationId || station.id || station._id || '';
  if (!baseId) return '';
  if (baseId.includes(':')) return baseId;
  const prefix = [station.countryCode, station.partyId].filter(Boolean).join(':');
  return prefix ? `${prefix}:${baseId}` : baseId;
};

/**
 * Resolves the assignment type for an RFID token ('GROUP' or 'INDIVIDUAL')
 * @param {RfidToken} tokenItem - RFID token entity
 * @returns {CardPoolKind} Resolved assignment type
 */
const resolveAssignmentType = (tokenItem: RfidToken): CardPoolKind => {
  const rawKind =
    tokenItem.cardPool?.kind ||
    tokenItem.businessType ||
    (tokenItem as any).assignmentType ||
    (tokenItem.cardPoolId ? 'GROUP' : 'INDIVIDUAL');
  if (rawKind === 'GROUP CARD' || rawKind === 'GROUP') {
    return 'GROUP';
  }
  return 'INDIVIDUAL';
};

/**
 * Details view and editor modal for an RFID token entity
 * @param {TokenDetailsModalProperties} properties - Component properties
 * @returns {React.JSX.Element} Token details modal content
 */
function TokenDetailsModal(properties: TokenDetailsModalProperties): React.JSX.Element {
  const {
    token,
    tokenId: tokenIdProperty,
    onClose,
    onDeactivate,
    onRecharge,
    onUpdate,
    isEditing: isEditingProperty,
    onEditModeChange,
  } = properties;
  const { t } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();

  const [currentToken, setCurrentToken] = useState<RfidToken>(token);
  const [tokenLoading, setTokenLoading] = useState<boolean>(false);
  const [stationList, setStationList] = useState<EmspStation[]>([]);
  const [stationsLoading, setStationsLoading] = useState<boolean>(true);
  const [customerList, setCustomerList] = useState<CustomerUser[]>([]);
  const [customersLoading, setCustomersLoading] = useState<boolean>(false);
  const [groupList, setGroupList] = useState<CardPool[]>([]);
  const [groupsLoading, setGroupsLoading] = useState<boolean>(false);
  const [internalEditing, setInternalEditing] = useState<boolean>(false);
  const isEditing = isEditingProperty !== undefined ? isEditingProperty : internalEditing;
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  /**
   * Sets edit mode state internally and notifies parent component if callback provided
   * @param {boolean} editing - Edit mode flag
   * @returns {void}
   */
  const handleSetEditing = (editing: boolean): void => {
    setInternalEditing(editing);
    if (onEditModeChange) {
      onEditModeChange(editing);
    }
  };

  const initialGroupKind: CardPoolKind = resolveAssignmentType(currentToken);
  const initialGroupId =
    currentToken.cardPoolId || currentToken.cardPool?._id || currentToken.cardPool?.id || '';
  const initialStationIds =
    currentToken.cardPool?.stationIds ||
    currentToken.stationIds ||
    (currentToken.stationId ? [currentToken.stationId] : []);
  const initialCustomerId =
    currentToken.linkedCustomerId ||
    currentToken.customerId ||
    currentToken.cardPool?.linkedCustomerId ||
    '';

  const [assignmentType, setAssignmentType] = useState<CardPoolKind>(initialGroupKind);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(initialGroupId);
  const [selectedStationIds, setSelectedStationIds] = useState<string[]>(initialStationIds);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(initialCustomerId);

  useEffect(() => {
    setCurrentToken(token);
    const kind: CardPoolKind = resolveAssignmentType(token);
    setAssignmentType(kind);
    const grpId = token.cardPoolId || token.cardPool?._id || token.cardPool?.id || '';
    setSelectedGroupId(grpId);
    const ids =
      token.cardPool?.stationIds || token.stationIds || (token.stationId ? [token.stationId] : []);
    setSelectedStationIds(ids);
    const custId =
      token.linkedCustomerId || token.customerId || token.cardPool?.linkedCustomerId || '';
    setSelectedCustomerId(custId);
    setValidationError(undefined);
  }, [token]);

  useEffect(() => {
    let isMounted = true;
    const targetTokenId = tokenIdProperty || token._id || token.id;

    if (targetTokenId) {
      setTokenLoading(true);
      rfidApi
        .fetchTokenById(targetTokenId)
        .then((response: any) => {
          if (!isMounted) return;
          const data =
            response?.data?.results || response?.data?.data || response?.data || response;
          if (data && typeof data === 'object') {
            let poolBalance: number | undefined;
            const rawPoolBalance = data?.cardPool?.balanceKwh ?? data?.balanceKwh;
            if (
              typeof rawPoolBalance === 'object' &&
              rawPoolBalance !== null &&
              '$numberDecimal' in rawPoolBalance
            ) {
              poolBalance = Number.parseFloat(rawPoolBalance.$numberDecimal) || 0;
            } else if (typeof rawPoolBalance === 'number') {
              poolBalance = rawPoolBalance;
            } else if (typeof rawPoolBalance === 'string') {
              poolBalance = Number.parseFloat(rawPoolBalance) || 0;
            }

            const cardPoolId =
              data?.cardPoolId || data?.cardPool?._id || data?.cardPool?.id || null;
            const cardPoolName = data?.cardPool?.name || data?.cardPoolName || null;
            const stationIds =
              data?.cardPool?.stationIds ||
              data?.stationIds ||
              (data?.stationId ? [data.stationId] : []);

            const formattedToken: RfidToken = {
              ...data,
              id: data?.id || data?._id || targetTokenId,
              _id: data?._id || data?.id || targetTokenId,
              cardPoolId,
              cardPoolName,
              balanceKwh: poolBalance !== undefined ? poolBalance : data?.balanceKwh,
              stationIds,
              createdAt: data?.createdAt ? ConvertUTCtoDate(data.createdAt) : '',
              deactivatedAt: data?.deactivatedAt ? ConvertUTCtoDate(data.deactivatedAt) : null,
            };

            setCurrentToken(formattedToken);
            const kind: CardPoolKind = resolveAssignmentType(formattedToken);
            setAssignmentType(kind);
            const grpId =
              formattedToken.cardPoolId ||
              formattedToken.cardPool?._id ||
              formattedToken.cardPool?.id ||
              '';
            setSelectedGroupId(grpId);
            const ids =
              formattedToken.cardPool?.stationIds ||
              formattedToken.stationIds ||
              (formattedToken.stationId ? [formattedToken.stationId] : []);
            setSelectedStationIds(ids);
            const custId =
              formattedToken.linkedCustomerId ||
              formattedToken.customerId ||
              formattedToken.cardPool?.linkedCustomerId ||
              '';
            setSelectedCustomerId(custId);
          }
        })
        .catch(() => {
          // Keep currentToken on failure
        })
        .finally(() => {
          if (isMounted) {
            setTokenLoading(false);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [token._id, token.id, tokenIdProperty]);

  const isActive = currentToken.status === 'ACTIVE';
  const isBusiness = currentToken.type === 'BUSINESS_TAP';
  const isPersonal = !isBusiness;
  const { cardPool } = currentToken;
  let groupKind = cardPool?.kind || currentToken.businessType || null;
  if (!groupKind && isBusiness) {
    groupKind = currentToken.cardPoolId ? 'GROUP' : 'INDIVIDUAL';
  }
  const isGroup = isBusiness && groupKind === 'GROUP';
  const isIndividual = isBusiness && groupKind === 'INDIVIDUAL';
  const canRecharge = Boolean(isIndividual && isActive && onRecharge);

  const cardPoolId = currentToken.cardPoolId || cardPool?._id || cardPool?.id || '';
  const cardPoolName = cardPool?.name || currentToken.cardPoolName || '';
  const rawBalance = cardPool?.balanceKwh ?? currentToken.balanceKwh;
  const balanceKwh = getNumericBalance(rawBalance);
  const stationIds =
    cardPool?.stationIds ||
    currentToken.stationIds ||
    (currentToken.stationId ? [currentToken.stationId] : []);
  const stationNames =
    cardPool?.stationNames ||
    currentToken.stationNames ||
    (currentToken.stationName ? [currentToken.stationName] : []);

  useEffect(() => {
    let isMounted = true;
    setStationsLoading(true);

    /**
     * Fetches available charging stations to resolve station names and operator details
     * @returns {Promise<void>} Resolves when stations are fetched
     */
    const loadStations = async (): Promise<void> => {
      try {
        const response = await rfidApi.fetchStationsList();
        if (isMounted) {
          const rawResults =
            response?.data?.results ||
            response?.data?.data ||
            (Array.isArray(response?.data) ? response?.data : []);
          const list: EmspStation[] = Array.isArray(rawResults) ? rawResults : [];
          setStationList(list);
        }
      } catch {
        // Gracefully fallback
      } finally {
        if (isMounted) {
          setStationsLoading(false);
        }
      }
    };

    loadStations();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (isBusiness) {
      setGroupsLoading(true);

      /**
       * Loads available card groups from EMSP API
       * @returns {Promise<void>} Resolves when card groups are loaded
       */
      const loadCardGroups = async (): Promise<void> => {
        try {
          const response = await rfidApi.fetchCardPools({ kind: 'GROUP' });
          if (isMounted) {
            const rawResults =
              response?.data?.results ||
              response?.data?.data ||
              (Array.isArray(response?.data) ? response?.data : []);
            const list: CardPool[] = Array.isArray(rawResults) ? rawResults : [];
            setGroupList(list);
          }
        } catch {
          // Gracefully fallback
        } finally {
          if (isMounted) {
            setGroupsLoading(false);
          }
        }
      };

      loadCardGroups();
    }

    return () => {
      isMounted = false;
    };
  }, [isBusiness]);

  useEffect(() => {
    let isMounted = true;

    if (isPersonal || isIndividual || (isBusiness && isEditing)) {
      setCustomersLoading(true);

      /**
       * Fetches available customer accounts from EMSP API
       * @returns {Promise<void>} Resolves when customers are loaded
       */
      const loadCustomers = async (): Promise<void> => {
        try {
          const response = await rfidApi.fetchCustomers();
          if (isMounted) {
            const rawResults =
              response?.data?.results ||
              response?.data?.data ||
              (Array.isArray(response?.data) ? response?.data : []);
            const list: CustomerUser[] = Array.isArray(rawResults) ? rawResults : [];
            setCustomerList(list);
          }
        } catch {
          // Gracefully fallback
        } finally {
          if (isMounted) {
            setCustomersLoading(false);
          }
        }
      };

      loadCustomers();
    }

    return () => {
      isMounted = false;
    };
  }, [isPersonal, isIndividual, isBusiness, isEditing]);

  const assignmentTypeOptions = [
    {
      label: t('cardGroup'),
      value: 'GROUP',
    },
    {
      label: t('individualCard'),
      value: 'INDIVIDUAL',
    },
  ];

  const groupOptions = groupList.map((group: CardPool) => {
    const groupId = group._id || group.id || '';
    const groupName = group.name || t('cardGroupValue');
    const balance = getNumericBalance(group.balanceKwh);
    const labelText = `${groupName} (${groupId}) - ${balance} kWh`;

    return {
      label: labelText,
      value: groupId,
      labelNode: (
        <div className="rfid-customer-select-item">
          <span className="rfid-customer-select-item__name">{groupName}</span>
          <span className="rfid-customer-select-item__id">{groupId}</span>
          <span className="rfid-customer-select-item__contact">{balance} kWh</span>
        </div>
      ),
    };
  });

  const selectedGroup = groupList.find((item) => (item._id || item.id) === selectedGroupId);

  const stationOptions: Option[] = stationList.map((item: EmspStation) => {
    const fullId = getFullStationId(item);
    const party = [item.countryCode, item.partyId].filter(Boolean).join(':');
    const displayName = item.name || fullId;
    const labelText = item.name
      ? `${item.name}${party ? ` (${party})` : ''} - ${fullId}`
      : `${fullId}`;

    return {
      ...item,
      label: labelText,
      value: fullId,
      labelNode: (
        <div className="rfid-customer-select-item">
          <span className="rfid-customer-select-item__name">{displayName}</span>
          <span className="rfid-customer-select-item__id">{fullId}</span>
          {party && <span className="rfid-customer-select-item__contact">{party}</span>}
        </div>
      ),
    };
  });

  const selectedStations = selectedStationIds.map((stationId, index) => {
    const matchedStation = stationList.find(
      (s) =>
        getFullStationId(s) === stationId ||
        s.stationId === stationId ||
        s._id === stationId ||
        s.id === stationId,
    );
    let displayName = '';
    if (matchedStation?.name && matchedStation.name !== stationId) {
      displayName = matchedStation.name;
    } else if (stationNames && stationNames[index] && stationNames[index] !== stationId) {
      displayName = stationNames[index];
    }

    const parts = stationId.split(':');
    let party = '';
    if (matchedStation) {
      party = [matchedStation.countryCode, matchedStation.partyId].filter(Boolean).join(':');
    } else if (parts.length >= 3) {
      party = `${parts[0]}:${parts[1]}`;
    }

    return {
      id: stationId,
      name: displayName,
      party,
    };
  });

  const customerOptions = customerList.map((item: CustomerUser) => {
    const contactInfo = [item.email, item.phoneNumber].filter(Boolean).join(' | ');
    const labelText = item.name
      ? `${item.name}${contactInfo ? ` (${contactInfo})` : ''} - ${item.userCustomId}`
      : `${item.userCustomId}${contactInfo ? ` (${contactInfo})` : ''}`;

    return {
      label: labelText,
      value: item.userCustomId,
      labelNode: (
        <div className="rfid-customer-select-item">
          <span className="rfid-customer-select-item__name">{item.name || item.userCustomId}</span>
          <span className="rfid-customer-select-item__id">{item.userCustomId}</span>
          {contactInfo && <span className="rfid-customer-select-item__contact">{contactInfo}</span>}
        </div>
      ),
    };
  });

  const currentCustomerId =
    currentToken.linkedCustomerId ||
    currentToken.customerId ||
    currentToken.cardPool?.linkedCustomerId ||
    '';
  const selectedCustomer = customerList.find((item) => item.userCustomId === selectedCustomerId);
  const currentCustomer = customerList.find(
    (item) => item.userCustomId === currentCustomerId || item.name === currentCustomerId,
  );

  /**
   * Handles deactivation action button click
   * @returns {void}
   */
  const handleDeactivate = (): void => {
    if (onDeactivate) {
      onDeactivate(currentToken);
    }
  };

  /**
   * Handles recharge action button click for Business Tap card group
   * @returns {void}
   */
  const handleRechargeClick = (): void => {
    if (onRecharge) {
      const targetGroup: CardPool = {
        id: cardPoolId || currentToken.id || currentToken._id || '',
        _id: cardPoolId || currentToken._id || currentToken.id || '',
        tenantId: currentToken.tenantId,
        kind: groupKind === 'INDIVIDUAL' ? 'INDIVIDUAL' : 'GROUP',
        name:
          cardPoolName ||
          (groupKind === 'INDIVIDUAL' ? t('individualTapCard') : t('cardGroupValue')),
        balanceKwh,
        stationIds,
        stationNames,
        linkedCustomerId: currentCustomerId,
        createdAt: currentToken.createdAt,
        updatedAt: currentToken.createdAt,
      };
      onRecharge(targetGroup);
    }
  };

  useEffect(() => {
    if (isEditing) {
      const kind: CardPoolKind = resolveAssignmentType(currentToken);
      setAssignmentType(kind);
      const grpId =
        currentToken.cardPoolId || currentToken.cardPool?._id || currentToken.cardPool?.id || '';
      setSelectedGroupId(grpId);
      if (isBusiness) {
        const ids =
          currentToken.cardPool?.stationIds ||
          currentToken.stationIds ||
          (currentToken.stationId ? [currentToken.stationId] : []);
        setSelectedStationIds(ids);
      }
      const custId =
        currentToken.linkedCustomerId ||
        currentToken.customerId ||
        currentToken.cardPool?.linkedCustomerId ||
        '';
      setSelectedCustomerId(custId);
      setValidationError(undefined);
    }
  }, [isEditing]);

  /**
   * Cancels edit mode, restores initial selections, and closes the side panel
   * @returns {void}
   */
  const handleCancelEdit = (): void => {
    const kind: CardPoolKind = resolveAssignmentType(currentToken);
    setAssignmentType(kind);
    const grpId =
      currentToken.cardPoolId || currentToken.cardPool?._id || currentToken.cardPool?.id || '';
    setSelectedGroupId(grpId);
    if (isBusiness) {
      const ids =
        currentToken.cardPool?.stationIds ||
        currentToken.stationIds ||
        (currentToken.stationId ? [currentToken.stationId] : []);
      setSelectedStationIds(ids);
    }
    const custId =
      currentToken.linkedCustomerId ||
      currentToken.customerId ||
      currentToken.cardPool?.linkedCustomerId ||
      '';
    setSelectedCustomerId(custId);
    setValidationError(undefined);
    handleSetEditing(false);
    onClose();
  };

  /**
   * Saves updated card group assignment for Business Group token
   * @returns {Promise<void>} Resolves when save operation completes
   */
  const handleSaveGroup = async (): Promise<void> => {
    if (!selectedGroupId) {
      setValidationError(t('groupRequired'));
      return;
    }

    setIsSaving(true);
    setValidationError(undefined);

    try {
      const tokenId = currentToken.id || currentToken._id || currentToken.uid || '';
      const updatedData = await dispatch(
        updateTokenApi({
          tokenId,
          data: {
            type: 'GROUP',
            cardPoolId: selectedGroupId,
          },
        }),
      ).unwrap();

      const matchedGroup = groupList.find((g) => (g._id || g.id) === selectedGroupId);

      const updatedToken: RfidToken = {
        ...currentToken,
        ...updatedData,
        cardPoolId: selectedGroupId,
        cardPoolName: matchedGroup?.name || currentToken.cardPoolName,
        cardPool: matchedGroup
          ? {
              ...matchedGroup,
              _id: matchedGroup._id || selectedGroupId,
              id: matchedGroup.id || selectedGroupId,
            }
          : currentToken.cardPool,
        businessType: 'GROUP',
      };

      setCurrentToken(updatedToken);
      handleSetEditing(false);

      if (onUpdate) {
        onUpdate(updatedToken);
      }
    } catch {
      // Error is handled in updateTokenApi thunk
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Saves updated station and customer assignments for Business Individual token
   * @returns {Promise<void>} Resolves when save operation completes
   */
  const handleSaveStations = async (): Promise<void> => {
    setIsSaving(true);
    setValidationError(undefined);

    try {
      const tokenId = currentToken.id || currentToken._id || currentToken.uid || '';
      const payloadCustomerId = selectedCustomerId.trim() ? selectedCustomerId.trim() : null;
      const updatedData = await dispatch(
        updateTokenApi({
          tokenId,
          data: {
            type: 'INDIVIDUAL',
            cardPoolId: null,
            stationIds: selectedStationIds.length > 0 ? selectedStationIds : undefined,
            linkedCustomerId: payloadCustomerId,
          },
        }),
      ).unwrap();

      const updatedToken: RfidToken = {
        ...currentToken,
        ...updatedData,
        cardPoolId: null,
        cardPoolName: null,
        stationIds: selectedStationIds,
        linkedCustomerId: payloadCustomerId,
        businessType: 'INDIVIDUAL',
        cardPool: currentToken.cardPool
          ? {
              ...currentToken.cardPool,
              kind: 'INDIVIDUAL',
              stationIds: selectedStationIds,
              linkedCustomerId: payloadCustomerId,
            }
          : currentToken.cardPool,
      };

      setCurrentToken(updatedToken);
      handleSetEditing(false);

      if (onUpdate) {
        onUpdate(updatedToken);
      }
    } catch {
      // Error is handled in updateTokenApi thunk
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Saves updated customer account assignment for Personal token
   * @returns {Promise<void>} Resolves when save operation completes
   */
  const handleSaveCustomer = async (): Promise<void> => {
    setIsSaving(true);
    setValidationError(undefined);

    try {
      const tokenId = currentToken.id || currentToken._id || currentToken.uid || '';
      const payloadCustomerId = selectedCustomerId.trim() ? selectedCustomerId.trim() : null;
      const updatedData = await dispatch(
        updateTokenApi({
          tokenId,
          data: { customerId: payloadCustomerId },
        }),
      ).unwrap();

      const updatedToken: RfidToken = {
        ...currentToken,
        ...updatedData,
        customerId: payloadCustomerId,
      };

      setCurrentToken(updatedToken);
      handleSetEditing(false);

      if (onUpdate) {
        onUpdate(updatedToken);
      }
    } catch {
      // Error is handled in updateTokenApi thunk
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Routes form submission to the appropriate handler based on card type and assignment type
   * @returns {Promise<void>} Resolves when submission finishes
   */
  const handleSaveForm = async (): Promise<void> => {
    if (isBusiness) {
      if (assignmentType === 'GROUP') {
        await handleSaveGroup();
      } else {
        await handleSaveStations();
      }
    } else {
      await handleSaveCustomer();
    }
  };

  /**
   * Renders the station list in view mode
   * @returns {React.JSX.Element} Station view content
   */
  const renderStationViewContent = (): React.JSX.Element => {
    if (stationsLoading) {
      return (
        <div className="rfid-stations-loading">
          <span className="rfid-stations-loading__spinner" />
          <span>{t('loadingStations')}</span>
        </div>
      );
    }

    if (stationIds.length === 0) {
      return (
        <div className="rfid-empty-card">
          <p className="rfid-empty-card__text">{t('noStationsAssigned')}</p>
        </div>
      );
    }

    return (
      <div className="rfid-selected-stations-list">
        <div className="rfid-selected-stations-list__items">
          {selectedStations.map((station) => (
            <div key={station.id} className="rfid-station-item-card">
              <div className="rfid-station-item-card__icon">
                <StationIcon />
              </div>
              <div className="rfid-station-item-card__info">
                {station.name ? (
                  <>
                    <span className="rfid-station-item-card__name">{station.name}</span>
                    <div className="rfid-station-item-card__sub-row">
                      <span className="rfid-station-item-card__id">{station.id}</span>
                      {station.party && (
                        <span className="rfid-station-item-card__party">{station.party}</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="rfid-station-item-card__sub-row">
                    <span className="rfid-station-item-card__id">{station.id}</span>
                    {station.party && (
                      <span className="rfid-station-item-card__party">{station.party}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Renders selected station cards when editing
   * @returns {React.JSX.Element | null} Selected stations cards
   */
  const renderEditStationSelection = (): React.JSX.Element | null => {
    if (stationsLoading) {
      return (
        <div className="rfid-stations-loading">
          <span className="rfid-stations-loading__spinner" />
          <span>{t('loadingStations')}</span>
        </div>
      );
    }

    if (selectedStations.length === 0) {
      return null;
    }

    return (
      <div className="rfid-selected-stations-list">
        <div className="rfid-selected-stations-list__header">
          <span className="rfid-selected-stations-list__title">
            {t('selectedStationsCount')} ({selectedStations.length})
          </span>
          {selectedStations.length > 1 && (
            <button
              type="button"
              className="rfid-selected-stations-list__clear-btn"
              onClick={() => setSelectedStationIds([])}>
              {t('clearAll')}
            </button>
          )}
        </div>
        <div className="rfid-selected-stations-list__items">
          {selectedStations.map((station) => (
            <div key={station.id} className="rfid-selected-user-card">
              <div className="rfid-selected-user-card__header">
                <span className="rfid-selected-user-card__name">{station.name || station.id}</span>
                <div className="rfid-selected-user-card__header-right">
                  {station.name && (
                    <span className="rfid-selected-user-card__id">{station.id}</span>
                  )}
                  <button
                    type="button"
                    className="rfid-selected-user-card__remove-btn"
                    onClick={() =>
                      setSelectedStationIds((previous) =>
                        previous.filter((id) => id !== station.id),
                      )
                    }
                    aria-label={`Remove ${station.name || station.id}`}
                    title={t('remove')}>
                    ✕
                  </button>
                </div>
              </div>
              {station.party && (
                <div className="rfid-selected-user-card__details">
                  <div className="rfid-selected-user-card__detail-item">
                    <span className="rfid-selected-user-card__detail-label">{t('operator')}:</span>
                    <span className="rfid-selected-user-card__detail-value">{station.party}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Renders the customer account information in view mode
   * @returns {React.JSX.Element} Customer view content
   */
  const renderCustomerViewContent = (): React.JSX.Element => {
    if (customersLoading) {
      return (
        <div className="rfid-stations-loading">
          <span className="rfid-stations-loading__spinner" />
          <span>{t('loadingCustomers')}</span>
        </div>
      );
    }

    if (currentCustomer) {
      return (
        <div className="rfid-selected-user-card">
          <div className="rfid-selected-user-card__header">
            <div className="rfid-selected-user-card__title-group">
              <span className="rfid-selected-user-card__avatar">
                <UserIcon />
              </span>
              <span className="rfid-selected-user-card__name">
                {currentCustomer.name || currentCustomer.userCustomId}
              </span>
            </div>
            <span className="rfid-selected-user-card__id">{currentCustomer.userCustomId}</span>
          </div>
          {(currentCustomer.email || currentCustomer.phoneNumber) && (
            <div className="rfid-selected-user-card__details">
              {currentCustomer.email && (
                <div className="rfid-selected-user-card__detail-item">
                  <span className="rfid-selected-user-card__detail-label">{t('email')}:</span>
                  <span className="rfid-selected-user-card__detail-value">
                    {currentCustomer.email}
                  </span>
                </div>
              )}
              {currentCustomer.phoneNumber && (
                <div className="rfid-selected-user-card__detail-item">
                  <span className="rfid-selected-user-card__detail-label">{t('phone')}:</span>
                  <span className="rfid-selected-user-card__detail-value">
                    {currentCustomer.phoneNumber}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    if (currentCustomerId) {
      return (
        <div className="rfid-selected-user-card">
          <div className="rfid-selected-user-card__header">
            <div className="rfid-selected-user-card__title-group">
              <span className="rfid-selected-user-card__avatar">
                <UserIcon />
              </span>
              <span className="rfid-selected-user-card__name">{currentCustomerId}</span>
            </div>
            <span className="rfid-selected-user-card__id">{currentCustomerId}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="rfid-empty-card">
        <p className="rfid-empty-card__text">{t('noCustomerAssigned')}</p>
      </div>
    );
  };

  // Determine whether to show the customer account section
  const showCustomerSection =
    (isPersonal && isEditing) ||
    (isBusiness && isEditing && assignmentType === 'INDIVIDUAL') ||
    (!isEditing && (isPersonal || isIndividual || Boolean(currentCustomerId)));

  let avatarClass = 'rfid-entity-details__avatar--blue';
  if (isBusiness) {
    const isGroupType = isEditing ? assignmentType === 'GROUP' : isGroup;
    avatarClass = isGroupType
      ? 'rfid-entity-details__avatar--purple'
      : 'rfid-entity-details__avatar--teal';
  }

  let isBusinessSaveDisabled = stationsLoading || customersLoading;
  if (assignmentType === 'GROUP') {
    isBusinessSaveDisabled = groupsLoading || !selectedGroupId;
  }

  const isSaveDisabled = isSaving || (isBusiness ? isBusinessSaveDisabled : customersLoading);

  if (tokenLoading && !currentToken.uid) {
    return (
      <div className="rfid-entity-details">
        <div className="rfid-entity-details__body">
          <div className="rfid-stations-loading">
            <span className="rfid-stations-loading__spinner" />
            <span>{t('loadingCardDetails')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rfid-entity-details">
      <div className="rfid-entity-details__body">
        {/* ── Header Banner ─────────────────────────────────── */}
        <div className="rfid-entity-details__header">
          <div className={`rfid-entity-details__avatar ${avatarClass}`}>
            <RfidTagIcon />
          </div>
          <div className="rfid-entity-details__header-text">
            <h3>{currentToken.uid}</h3>
            <div className="rfid-entity-details__badges">
              <span
                className={`rfid-type-badge ${
                  isBusiness ? 'rfid-type-badge--business' : 'rfid-type-badge--personal'
                }`}>
                {isBusiness ? t('businessTap') : t('personalTap')}
              </span>
              {isBusiness && groupKind && (
                <span
                  className={`rfid-kind-badge ${
                    groupKind === 'GROUP' ? 'rfid-kind-badge--group' : 'rfid-kind-badge--individual'
                  }`}>
                  {groupKind === 'GROUP' ? t('cardGroupValue') : t('individual')}
                </span>
              )}
              {isBusiness && (
                <span className="rfid-balance-tag numeric-unit" dir="ltr">
                  {balanceKwh.toFixed(2)} kWh
                </span>
              )}
              <span
                className={`rfid-status-badge ${
                  isActive ? 'rfid-status-badge--active' : 'rfid-status-badge--deactivated'
                }`}>
                <span className="rfid-status-badge__dot" />
                {currentToken.status}
              </span>
            </div>
          </div>
        </div>

        {/* ── Card Information Grid (Only Related Data) ─────── */}
        <div className="rfid-entity-details__grid">
          <div className="rfid-entity-details__item">
            <span className="rfid-entity-details__label">{t('tokenId')}</span>
            <span className="rfid-entity-details__value rfid-entity-details__mono">
              {currentToken.id || currentToken._id || currentToken.uid}
            </span>
          </div>

          <div className="rfid-entity-details__item">
            <span className="rfid-entity-details__label">{t('tenantId')}</span>
            <span className="rfid-entity-details__value rfid-entity-details__mono">
              {currentToken.tenantId}
            </span>
          </div>

          <div className="rfid-entity-details__item">
            <span className="rfid-entity-details__label">{t('issuedBy')}</span>
            <span className="rfid-entity-details__value">{currentToken.issuedBy}</span>
          </div>

          {/* Group-specific attributes */}
          {isGroup && !isEditing && (
            <>
              <div className="rfid-entity-details__item">
                <span className="rfid-entity-details__label">{t('cardGroupName')}</span>
                <span className="rfid-entity-details__value">
                  {cardPoolName || t('cardGroupValue')}
                </span>
              </div>

              <div className="rfid-entity-details__item">
                <span className="rfid-entity-details__label">{t('cardGroupId')}</span>
                <span className="rfid-entity-details__value rfid-entity-details__mono">
                  {cardPoolId || '—'}
                </span>
              </div>

              <div className="rfid-entity-details__item">
                <span className="rfid-entity-details__label">{t('groupBalance')}</span>
                <span className="rfid-entity-details__value">{balanceKwh.toFixed(2)} kWh</span>
              </div>
            </>
          )}

          {/* Individual business balance */}
          {isIndividual && !isEditing && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">{t('cardBalance')}</span>
              <span className="rfid-entity-details__value">{balanceKwh.toFixed(2)} kWh</span>
            </div>
          )}

          <div className="rfid-entity-details__item">
            <span className="rfid-entity-details__label">{t('createdDate')}</span>
            <span className="rfid-entity-details__value">{currentToken.createdAt}</span>
          </div>

          {currentToken.deactivatedAt && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">{t('deactivatedDate')}</span>
              <span className="rfid-entity-details__value">{currentToken.deactivatedAt}</span>
            </div>
          )}
        </div>

        {/* ── Business Card Assignment Type Selection (when editing Business Card) ── */}
        {isEditing && isBusiness && (
          <div className="rfid-entity-details__section">
            <div className="rfid-entity-details__section-header">
              <span className="rfid-entity-details__section-title">{t('assignmentType')}</span>
            </div>
            <div className="rfid-modal-form__field">
              <SelectField
                inputLabel={t('assignmentType')}
                name="assignmentType"
                options={assignmentTypeOptions}
                selectedValue={assignmentType}
                valueRequired
                onChangeValue={(_, value) => {
                  setAssignmentType(value as CardPoolKind);
                  setValidationError(undefined);
                }}
              />
            </div>
          </div>
        )}

        {/* ── Card Group Selection (when editing Business Card with Group assignment) ── */}
        {isEditing && isBusiness && assignmentType === 'GROUP' && (
          <div className="rfid-entity-details__section">
            <div className="rfid-entity-details__section-header">
              <span className="rfid-entity-details__section-title">{t('cardGroup')}</span>
            </div>
            <div className="rfid-modal-form__field">
              <SelectField
                inputLabel={t('cardGroup')}
                name="selectedGroupId"
                options={groupOptions}
                selectedValue={selectedGroupId}
                valueRequired
                error={Boolean(validationError)}
                errorMessage={validationError}
                onChangeValue={(_, value) => {
                  setSelectedGroupId(String(value));
                  if (validationError) {
                    setValidationError(undefined);
                  }
                }}
                loading={groupsLoading}
                placeholder={groupsLoading ? t('loadingCardGroups') : t('selectCardGroup')}
              />
              {validationError && <span className="text-danger">{validationError}</span>}

              {selectedGroup && (
                <div className="rfid-selected-user-card">
                  <div className="rfid-selected-user-card__header">
                    <span className="rfid-selected-user-card__name">
                      {selectedGroup.name || t('cardGroupValue')}
                    </span>
                    <span className="rfid-selected-user-card__id">
                      {selectedGroup._id || selectedGroup.id}
                    </span>
                  </div>
                  <div className="rfid-selected-user-card__details">
                    <div className="rfid-selected-user-card__detail-item">
                      <span className="rfid-selected-user-card__detail-label">
                        {t('availableBalance')}:
                      </span>
                      <span className="rfid-selected-user-card__detail-value">
                        {getNumericBalance(selectedGroup.balanceKwh)} kWh
                      </span>
                    </div>
                    {Array.isArray(selectedGroup.stationIds) &&
                      selectedGroup.stationIds.length > 0 && (
                        <div className="rfid-selected-user-card__detail-item">
                          <span className="rfid-selected-user-card__detail-label">
                            {t('stations')}:
                          </span>
                          <span className="rfid-selected-user-card__detail-value">
                            {selectedGroup.stationIds.length} {t('authorized')}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Linked Customer Account Section ────────────────── */}
        {showCustomerSection && (
          <div className="rfid-entity-details__section">
            <div className="rfid-entity-details__section-header">
              <span className="rfid-entity-details__section-title">
                <UserIcon />
                {isEditing ? t('editCustomerAccount') : t('customerAccount')}
              </span>
            </div>

            {isEditing ? (
              <div className="rfid-edit-stations-container">
                <SelectField
                  inputLabel={t('customerAccount')}
                  name="selectedCustomerId"
                  options={customerOptions}
                  selectedValue={selectedCustomerId}
                  error={Boolean(validationError)}
                  errorMessage={validationError}
                  onChangeValue={(_, value) => {
                    setSelectedCustomerId(String(value));
                    if (validationError) {
                      setValidationError(undefined);
                    }
                  }}
                  loading={customersLoading}
                  placeholder={
                    customersLoading ? t('loadingCustomers') : t('selectCustomerAccount')
                  }
                />
                {validationError && <span className="text-danger">{validationError}</span>}

                {selectedCustomer && (
                  <div className="rfid-selected-user-card">
                    <div className="rfid-selected-user-card__header">
                      <div className="rfid-selected-user-card__title-group">
                        <span className="rfid-selected-user-card__avatar">
                          <UserIcon />
                        </span>
                        <span className="rfid-selected-user-card__name">
                          {selectedCustomer.name || selectedCustomer.userCustomId}
                        </span>
                      </div>
                      <div className="rfid-selected-user-card__header-right">
                        <span className="rfid-selected-user-card__id">
                          {selectedCustomer.userCustomId}
                        </span>
                        <button
                          type="button"
                          className="rfid-selected-user-card__remove-btn"
                          onClick={() => setSelectedCustomerId('')}
                          aria-label={`Remove ${selectedCustomer.name || selectedCustomer.userCustomId}`}
                          title={t('remove')}>
                          ✕
                        </button>
                      </div>
                    </div>
                    {(selectedCustomer.email || selectedCustomer.phoneNumber) && (
                      <div className="rfid-selected-user-card__details">
                        {selectedCustomer.email && (
                          <div className="rfid-selected-user-card__detail-item">
                            <span className="rfid-selected-user-card__detail-label">
                              {t('email')}:
                            </span>
                            <span className="rfid-selected-user-card__detail-value">
                              {selectedCustomer.email}
                            </span>
                          </div>
                        )}
                        {selectedCustomer.phoneNumber && (
                          <div className="rfid-selected-user-card__detail-item">
                            <span className="rfid-selected-user-card__detail-label">
                              {t('phone')}:
                            </span>
                            <span className="rfid-selected-user-card__detail-value">
                              {selectedCustomer.phoneNumber}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              renderCustomerViewContent()
            )}
          </div>
        )}

        {/* ── Authorized Charging Stations (Business Cards) ──── */}
        {isBusiness && (!isEditing || (isEditing && assignmentType === 'INDIVIDUAL')) && (
          <div className="rfid-entity-details__section">
            <div className="rfid-entity-details__section-header">
              <span className="rfid-entity-details__section-title">
                <StationIcon />
                {isEditing ? t('editAuthorizedStations') : t('authorizedStations')}
                {!isEditing && (
                  <span className="rfid-entity-details__count-pill">{stationIds.length}</span>
                )}
              </span>
            </div>

            {isEditing ? (
              <div className="rfid-edit-stations-container">
                <MultiSelectDropdown
                  title={t('chargingStations')}
                  name="selectedStationIds"
                  options={stationOptions}
                  selectedValues={selectedStationIds}
                  drillDownLevels={['countryCode', 'partyId']}
                  disabled={stationsLoading}
                  error={Boolean(validationError)}
                  errorMessage={validationError}
                  onSelectionChange={(_, values) => {
                    setSelectedStationIds(values);
                    if (validationError) {
                      setValidationError(undefined);
                    }
                  }}
                  placeholder={
                    stationsLoading ? t('loadingStations') : t('selectStationsPlaceholder')
                  }
                  placeholderFilled={
                    selectedStationIds.length > 1
                      ? `${selectedStationIds.length} ${t('stations')}`
                      : t('station')
                  }
                  search
                />
                {validationError && <span className="text-danger">{validationError}</span>}
                {renderEditStationSelection()}
              </div>
            ) : (
              renderStationViewContent()
            )}
          </div>
        )}
      </div>

      {/* ── Bottom Sticky Action Buttons ───────────────────── */}
      <div className="rfid-entity-details__actions">
        {isEditing ? (
          <div className="rfid-entity-details__actions--edit">
            <MyButton
              label={isSaving ? t('saving') : t('saveChanges')}
              buttonType="primary"
              onClickFunc={handleSaveForm}
              disableButton={isSaveDisabled}
              loading={isSaving}
            />
            <MyButton
              label={t('cancel', { defaultValue: 'Cancel' })}
              buttonType="outline"
              onClickFunc={handleCancelEdit}
              disableButton={isSaving}
            />
          </div>
        ) : (
          <div
            className={`rfid-entity-details__actions--buttons ${
              canRecharge ? 'rfid-entity-details__actions--buttons--has-recharge' : ''
            }`}>
            {canRecharge && (
              <MyButton
                label={t('rechargeBalance')}
                buttonType="primary"
                onClickFunc={handleRechargeClick}
              />
            )}
            {onDeactivate && (
              <MyButton
                label={isActive ? t('deactivateCard') : t('activateCard')}
                buttonType="danger-outline"
                onClickFunc={handleDeactivate}
              />
            )}
            <MyButton label={t('close')} buttonType="outline" onClickFunc={onClose} />
          </div>
        )}
      </div>
    </div>
  );
}

export default TokenDetailsModal;
